import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import type { DirectionWinRateBar } from "./chart-data";
import { CHART_COLORS, CHART_FONT, CSS_VARS, useChartTheme } from "./chart-theme";

interface Props {
	data: DirectionWinRateBar[];
}

const CustomTooltip = ({
	active,
	payload,
}: {
	active?: boolean;
	payload?: Array<{ payload: DirectionWinRateBar }>;
}) => {
	if (!active || !payload?.[0]) return null;
	const p = payload[0].payload;
	return (
		<div
			className="rounded-md border px-2.5 py-1.5 text-[0.72rem] shadow-lg"
			style={{
				background: CSS_VARS.tooltip,
				borderColor: CSS_VARS.tooltipBorder,
			}}
		>
			<p className="font-semibold tg-text-primary">{p.direction}</p>
			<p style={{ color: CHART_COLORS.green }} className="font-semibold">
				Win Rate: {p.winRatePercent}%
			</p>
			<p className="tg-text-muted">
				{p.total} trades ({p.wins}W / {p.losses}L)
			</p>
			<p
				style={{
					color: p.netPL >= 0 ? CHART_COLORS.green : CHART_COLORS.red,
				}}
			>
				Net P/L: {p.netPL >= 0 ? "+" : ""}${p.netPL.toFixed(2)}
			</p>
		</div>
	);
};

const barColor = (winRate: number): string => {
	if (winRate >= 0.6) return CHART_COLORS.green;
	if (winRate >= 0.4) return CHART_COLORS.yellow;
	return CHART_COLORS.red;
};

export const DirectionWinRateChart = ({ data }: Props) => {
	const theme = useChartTheme();

	if (data.length === 0) {
		return (
			<p className="flex h-full items-center justify-center text-[0.78rem] tg-text-muted">
				No direction data available.
			</p>
		);
	}

	return (
		<ResponsiveContainer width="100%" height="100%">
			<BarChart
				data={data}
				margin={{ top: 8, right: 12, bottom: 4, left: 0 }}
			>
				<CartesianGrid
					strokeDasharray="3 3"
					stroke={theme.grid}
					vertical={false}
				/>
				<XAxis
					dataKey="direction"
					tick={{ fill: theme.axis, fontSize: CHART_FONT.size }}
					tickLine={false}
					axisLine={{ stroke: theme.grid }}
				/>
				<YAxis
					tick={{ fill: theme.axis, fontSize: CHART_FONT.size }}
					tickLine={false}
					axisLine={false}
					width={40}
					tickFormatter={(v: number) => `${v}%`}
					domain={[0, 100]}
				/>
				<Tooltip
					content={<CustomTooltip />}
					cursor={{ fill: theme.grid, fillOpacity: 0.5 }}
				/>
				<Bar dataKey="winRatePercent" radius={[4, 4, 0, 0]} maxBarSize={56} isAnimationActive={false}>
					{data.map((entry) => (
						<Cell
							key={entry.direction}
							fill={barColor(entry.winRate)}
						/>
					))}
				</Bar>
			</BarChart>
		</ResponsiveContainer>
	);
};
